import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { FiMail, FiCheck, FiAlertTriangle, FiArrowLeft } from "react-icons/fi";
import authService from "../../services/authService";
import { isEmailValid } from "../../utils/validationUtils";
import { AuthLayout, AuthInput, AuthButton } from "./components";
import styles from "./Auth.module.css";

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const [cooldown, setCooldown] = useState(0);

  // Handle cooldown timer
  useEffect(() => {
    let intervalId = null;
    if (cooldown > 0) {
      intervalId = setInterval(() => {
        setCooldown((prev) => prev - 1);
      }, 1000);
    }
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [cooldown]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email.trim()) {
      setError("Please enter your email address");
      return;
    }

    if (!isEmailValid(email)) {
      setError("Please enter a valid email address");
      return;
    }

    try {
      setIsSubmitting(true);
      setError("");

      await authService.forgotPassword({ email });

      // Always show success (for security)
      setSuccess(true);
      setCooldown(60);
    } catch (err) {
      // For security, still show success
      setSuccess(true);
      setCooldown(60);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResend = async () => {
    if (cooldown > 0) return;

    try {
      setIsSubmitting(true);
      setError("");

      await authService.forgotPassword({ email });
      setCooldown(60);
    } catch (err) {
      setCooldown(60);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Success State
  if (success) {
    return (
      <AuthLayout
        icon={<FiCheck size={28} />}
        title="Email Sent"
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <p style={{ textAlign: 'center', color: 'var(--au-text-secondary)', marginBottom: '1rem' }}>
            We've sent recovery instructions to:
          </p>
          <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
            <span className={styles['au-email-display']}>{email}</span>
          </div>

          <div className={`${styles['au-alert']} ${styles['au-alert--info']}`}>
            <span>Please check your inbox and spam folder. The reset link will expire in 10 minutes.</span>
          </div>

          {/* Resend Section */}
          <div className={styles['au-resend']} style={{ marginTop: '1.5rem' }}>
            {cooldown > 0 ? (
              <span>You can request another email in {cooldown}s</span>
            ) : (
              <button
                type="button"
                className={styles['au-resend__btn']}
                onClick={handleResend}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Sending..." : "Resend Recovery Email"}
              </button>
            )}
          </div>

          {/* Back to Login */}
          <div style={{ textAlign: 'center', marginTop: '2rem' }}>
            <Link to="/signin" className={styles['au-back-link']}>
              <FiArrowLeft size={16} />
              <span>Back to Login</span>
            </Link>
          </div>
        </motion.div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      icon={<FiMail size={28} />}
      title="Forgot Password"
      subtitle="Enter your email to receive password reset instructions"
      footer={
        <Link to="/signin" className={styles['au-back-link']}>
          <FiArrowLeft size={16} />
          <span>Back to Login</span>
        </Link>
      }
    >
      {/* Error Alert */}
      <AnimatePresence>
        {error && (
          <motion.div
            className={`${styles['au-alert']} ${styles['au-alert--error']}`}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
          >
            <FiAlertTriangle className={styles['au-alert__icon']} />
            <span>{error}</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Form */}
      <form onSubmit={handleSubmit} className={styles['au-form']}>
        <AuthInput
          icon={FiMail}
          label="Email Address"
          type="email"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (error) setError("");
          }}
          placeholder="Enter your registered email"
          required
          disabled={isSubmitting}
        />

        <AuthButton
          type="submit"
          variant="primary"
          fullWidth
          loading={isSubmitting}
        >
          {isSubmitting ? "Sending..." : "Reset Password"}
        </AuthButton>
      </form>
    </AuthLayout>
  );
};

export default ForgotPassword;
